import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '@app/app.service';
import { ChangeStepService } from '@app/core/components/stepper/service/change-step.service';
import { CoreModule } from '@app/core/core.module';
import { WorkingStatus } from '@app/shared/enums/working-status';
import { Profession } from '@app/shared/interfaces/common-data/profession';
import { ProfessionalSector } from '@app/shared/interfaces/common-data/professional-sector';
import { LabelCode } from '@app/shared/interfaces/label-code.interface';
import { ChangeDeviceService } from '@app/shared/services/change-device.service';
import { PersonalDataService } from '@app/shared/services/common-data/personal-data.service';
import { SaveDataService } from '@app/shared/services/save-data.service';
import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { SelectButtonOptionClickEvent } from 'primeng/selectbutton';

@Component({
    selector: 'app-working-situation',
    standalone: true,
    imports: [CoreModule, CommonModule, SharedModule, TranslateModule],
    templateUrl: './working-situation.component.html',
    styleUrl: './working-situation.component.scss',
})
export class WorkingSituationComponent {

    private saveDataService = inject(SaveDataService);
    private appData = inject(AppService);

    continueButton = 'common.buttons.continueButton';
    backButton = 'common.buttons.backButton';

    workingSituationTitle = 'features.workingSituation.workingSituationTitle';
    workingSituationSubtitle = 'features.workingSituation.workingSituationSubtitle';

    //working
    companyName = 'features.workingSituation.companyName';
    sectorText = 'features.workingSituation.sectorText';
    branchText = 'features.workingSituation.branchText';
    professionText = 'features.workingSituation.professionText';
    //no working
    situationText = 'features.workingSituation.situationText';
    monthlyNetIncomeLabel = 'features.workingSituation.monthlyNetIncomeLabel';
    lastJobText = 'features.workingSituation.lastJobText';

    backButtonDisabled = false;
    continueButtonDisabled = true;

    isPortraitDevice = false;

    formGroup!: FormGroup;

    workStatusOptions: LabelCode[] = [
        { label: 'Trabajo', code: WorkingStatus.WORKING },
        { label: 'No trabajo', code: WorkingStatus.NOT_WORKING },
    ];

    selectedWorkStatus?: LabelCode;
    showForm: boolean = false;
    workingSelected: boolean = false;

	professions: Profession[] = [];
    professionsFiltered: Profession[] = [];
    branches: Profession[] = [];
    professionalSectors: ProfessionalSector[] = [];
    selectedProfessionalSector?: ProfessionalSector;
    selectedBranch?: Profession;
    selectedProfession?: Profession;

    notWorkingSituationOptions: Profession[] = [];
    selectedNotWorkingSituation?: Profession;
    selectedSituation?: Profession;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private changeDeviceService: ChangeDeviceService,
        private changeStepService: ChangeStepService,
        private fb: FormBuilder,
        private personalDataService: PersonalDataService,
    ) {
        this.changeStepService.changeStep(8);
    }

    ngOnInit() {
        this.isPortraitDevice = this.changeDeviceService.isPortraitDevice;
        this.changeDeviceService.changedDevice.subscribe((value) => {
            this.isPortraitDevice = value;
        });

        this.createForm();
        this.setValuesForm(this.appData.data.userData.workingSituation);
        this.getProfessionalSectors();
        this.getSituations();
        this.formIsValid();
    }

    createForm() {

        this.workStatusOptions = [
            { label: 'Trabajo', code: WorkingStatus.WORKING },
            { label: 'No trabajo', code: WorkingStatus.NOT_WORKING },
        ];

        if (this.showForm) {

            if (this.workingSelected) {

                this.selectedWorkStatus = { label: 'Trabajo', code: WorkingStatus.WORKING };

                this.formGroup = this.fb.group({
                    workStatus: new FormControl(this.selectedWorkStatus ? this.selectedWorkStatus.code : WorkingStatus.WORKING, [Validators.required]),
                    sector: new FormControl(null, [Validators.required]),
                    branch: new FormControl(null, [Validators.required]),
                    profession: new FormControl(null, [Validators.required]),
                    company: new FormControl('', [Validators.required, Validators.minLength(1)]),
                });
            } else {

                this.selectedWorkStatus = { label: 'No trabajo', code: WorkingStatus.NOT_WORKING };

                this.formGroup = this.fb.group({
                    workStatus: new FormControl(this.selectedWorkStatus ? this.selectedWorkStatus.code : WorkingStatus.NOT_WORKING, [Validators.required]),
                    notWorkingSituation: new FormControl(null, [Validators.required]),
                    revenue: new FormControl('', [Validators.required, Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                    lastJob: new FormControl('', [Validators.required, Validators.minLength(1)]),
                });
            }
            this.formGroup.get('branch')?.disable();
            this.formGroup.get('profession')?.disable();
        } else {
            this.formGroup = this.fb.group({
                workStatus: new FormControl(this.selectedWorkStatus ? this.selectedWorkStatus.code : '', [Validators.required]),
            });
        }
    }

    setValuesForm(value?: any) {
        if (value != undefined) {

            if(value.notWorkingSituation != undefined){
                this.selectedNotWorkingSituation = {
                    sectorCode: value.notWorkingSituation.sectorCode ? value.notWorkingSituation.sectorCode : '',
                    sectorDescription: value.notWorkingSituation.sectorDescription ? value.notWorkingSituation.sectorDescription: '',
                    fieldCode: value.notWorkingSituation.fieldCode ? value.notWorkingSituation.fieldCode : '',
                    fieldDescription: value.notWorkingSituation.fieldDescription ? value.notWorkingSituation.fieldDescription : '',
                    professionCode: value.notWorkingSituation.professionCode ? value.notWorkingSituation.professionCode : '',
                    professionDescription: value.notWorkingSituation.professionDescription ? value.notWorkingSituation.professionDescription : '',
                    bankProfessionalStatusCode: value.notWorkingSituation.bankProfessionalStatusCode ? value.notWorkingSituation.bankProfessionalStatusCode : '',
                    bankProfessionalStatusDescription: value.notWorkingSituation.bankProfessionalStatusDescription ? value.notWorkingSituation.bankProfessionalStatusDescription : ''
                };
            }

            if (value.sector !=undefined){
                this.selectedProfessionalSector = value.sector;
            }

            if(value.branch != undefined){
                this.selectedBranch = {
                    sectorCode: value.branch.sectorCode ? value.branch.sectorCode : '',
                    sectorDescription: value.branch.sectorDescription ? value.branch.sectorDescription: '',
                    fieldCode: value.branch.fieldCode ? value.branch.fieldCode : '',
                    fieldDescription: value.branch.fieldDescription ? value.branch.fieldDescription : '',
                    professionCode: value.branch.professionCode ? value.branch.professionCode : '',
                    professionDescription: value.branch.professionDescription ? value.branch.professionDescription : '',
                    bankProfessionalStatusCode: value.branch.bankProfessionalStatusCode,
                    bankProfessionalStatusDescription: value.branch.bankProfessionalStatusDescription,
                };
            }

            if(value.profession != undefined){
                this.selectedProfession = {
                    sectorCode: value.profession.sectorCode ? value.profession.sectorCode : '',
                    sectorDescription: value.profession.sectorDescription ? value.profession.sectorDescription: '',
                    fieldCode: value.profession.fieldCode ? value.profession.fieldCode : '',
                    fieldDescription: value.profession.fieldDescription ? value.profession.fieldDescription : '',
                    professionCode: value.profession.professionCode ? value.profession.professionCode : '',
                    professionDescription: value.profession.professionDescription ? value.profession.professionDescription : '',
                    bankProfessionalStatusCode: value.profession.bankProfessionalStatusCode,
                    bankProfessionalStatusDescription: value.profession.bankProfessionalStatusDescription,
                };
            }

            if(value.workStatus != undefined && value.workStatus != ''){
                 // Emitir el evento
                const event: SelectButtonOptionClickEvent = {
                    originalEvent: new Event('click'),
                    option: this.workStatusOptions.find(option => option.code === value.workStatus)
                };
                this.onChangeWorkStatus(event);
            }

            if(value.workStatus == WorkingStatus.WORKING){
                this.formGroup = this.fb.group({
                    workStatus: new FormControl(this.selectedWorkStatus ? this.selectedWorkStatus.code : WorkingStatus.WORKING, [Validators.required]),
                    sector: new FormControl(value.sector, [Validators.required]),
                    branch: new FormControl(this.selectedBranch , [Validators.required]),
                    profession: new FormControl(this.selectedProfession, [Validators.required]),
                    company: new FormControl(value.company, [Validators.required, Validators.minLength(1)]),
                });
            } else {
                this.formGroup = this.fb.group({
                    workStatus: new FormControl(value.workStatus ? value.workStatus : '', [Validators.required]),
                    notWorkingSituation: new FormControl(value.notWorkingSituation, [Validators.required]),
                    revenue: new FormControl(value.revenue, [Validators.required, Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                    lastJob: new FormControl(value.lastJob, [Validators.required, Validators.minLength(1)]),
                });
            }


        }
    }

    getProfessionalSectors() {
        this.personalDataService.getProfessionalSectors().subscribe({
            next: (response) => {
                this.professionalSectors = response.filter((sector) => sector.code !== 'MIXED');
                this.professionalSectors = this.professionalSectors.sort((a, b) => (a.description < b.description ? -1 : 1));
                if(this.selectedProfessionalSector != undefined) {
                    this.selectedProfessionalSector = this.findProfessionalSectorsBySectorCode(this.selectedProfessionalSector!.code);
                    this.formGroup.patchValue({
                        sector: this.selectedProfessionalSector
                    });
                    this.getBranchesBySector(this.selectedProfessionalSector?.code);
                }
            },
            error: (error) => console.log(error),
        });
    }

    findProfessionalSectorsBySectorCode(code : string): ProfessionalSector | undefined{
        if (!this.professionalSectors.length)
            return undefined;
        return this.professionalSectors.find(profesion => profesion.code === code);
    }

    getBranchesBySector(sectorCode?: string) {
        this.personalDataService.getProfessions({ sectorCode: sectorCode }).subscribe({
            next: (response) => {
                this.branches = response
                                    .filter(item => item.sectorCode === this.selectedProfessionalSector?.code)
                                    .filter(item => item.fieldDescription !== undefined && item.fieldDescription.trim() !== "")
                                    .filter((item, index, self) =>  index === self.findIndex(t => t.fieldDescription === item.fieldDescription));
                this.branches = this.branches.sort((a, b) => (a.fieldDescription < b.fieldDescription ? -1 : 1));
                this.professions = response
                                    .filter(item => item.sectorCode === this.selectedProfessionalSector?.code)
                                   // .filter(item => item.professionDescription !== undefined && item.professionDescription.trim() !== "")
                                    .filter((item, index, self) =>  index === self.findIndex(t => t.professionDescription === item.professionDescription));
                this.professionsFiltered = this.professions.sort((a, b) => (a.professionDescription < b.professionDescription ? -1 : 1));

                if (this.selectedBranch != undefined){
                    this.selectedBranch = this.findBranchByCode(this.selectedProfessionalSector!.code);
                    this.selectedProfession = this.findProfessionsByBranch(this.selectedBranch?.fieldCode);
                    this.formGroup.patchValue({
                        branch: this.selectedBranch,
                        profession: this.selectedProfession
                    });

                }
            },
            error: (error) => console.log(error),
        });
    }

    findBranchByCode(code : string): Profession | undefined{
        if (!this.professions.length)
            return undefined;
        return this.professions.find(profesion => profesion.sectorCode === code);
    }

    getFilteredProfessionsByBranch(branchCode?: string) {
        return this.branches.filter((profession) => profession.fieldCode === branchCode);
    }

    findProfessionsByBranch(branchCode?: string) {
        return this.professionsFiltered.find((profession) => profession.fieldCode === branchCode);
    }

    getSituations() {
        this.personalDataService.getProfessions({ sectorCode: 'MIXED' }).subscribe({
            next: (response) => {
                this.notWorkingSituationOptions = response.sort((a, b) => (a.professionDescription < b.professionDescription ? -1 : 1));
                if(this.selectedSituation != undefined && this.selectedSituation!.sectorCode){
                    this.selectedSituation = this.findNotWorkingSituationBySectorCode(this.selectedSituation!.sectorCode);
                }
                if(this.selectedNotWorkingSituation != undefined && this.selectedNotWorkingSituation.professionCode){
                    this.selectedSituation = this.findNotWorkingSituationByProfessionCode(this.selectedNotWorkingSituation!.professionCode);
                    this.formGroup.patchValue({
                        notWorkingSituation: this.selectedSituation,
                    });
                }
            },
            error: (error) => console.log(error),
        });
    }

    findNotWorkingSituationBySectorCode(code : string): Profession | undefined{
        if (!this.notWorkingSituationOptions || this.notWorkingSituationOptions.length == 0)
            return undefined;
        return this.notWorkingSituationOptions.find(profesion => profesion.sectorCode === code);
    }

    findNotWorkingSituationByProfessionCode(code : string): Profession | undefined{
        if (!this.notWorkingSituationOptions.length)
            return undefined;
        return this.notWorkingSituationOptions.find(profesion => profesion.professionCode === code);
    }


    //-------------------------//
    //-------ON CHANGES--------//
    //-------------------------//
    //-FALTA

    onChangeWorkStatus(event: SelectButtonOptionClickEvent) {

        console.log(event);

        this.showForm = true;
        this.selectedWorkStatus = event.option;

        if (event.option.code == WorkingStatus.WORKING) this.workingSelected = true;
        else this.workingSelected = false;

        this.createForm();
        this.formIsValid();
    }

/*
    onChangeWorkStatus(event: SelectButtonOptionClickEvent | undefined) {

        console
        this.showForm = true;
        if (event != undefined) this.selectedWorkStatus = event.option;
        if (this.selectedWorkStatus?.code == WorkingStatus.WORKING) {
            this.workingSelected = true;
        } else if (this.selectedWorkStatus?.code == WorkingStatus.NOT_WORKING) {
            this.workingSelected = false;
        }
        this.createForm();
        this.formIsValid();
    }
 */
    onChangeSector(event: DropdownChangeEvent) {
        this.selectedProfessionalSector = event.value;
        this.getBranchesBySector(this.selectedProfessionalSector?.code);
        this.selectedBranch = undefined;
        this.formGroup.get('branch')?.reset();
        this.formGroup.get('branch')?.enable();
        this.selectedProfession = undefined;
        this.formGroup.get('profession')?.reset();
        this.formGroup.get('profession')?.disable();
        this.formIsValid();
    }

    onChangeBranch(event: DropdownChangeEvent) {
        this.selectedBranch = event.value;
        this.professions = this.getFilteredProfessionsByBranch(this.selectedBranch?.fieldCode);
        this.selectedProfession = undefined;
        this.formGroup.get('profession')?.reset();
        this.formGroup.get('profession')?.enable();
        this.formIsValid();
    }

    onChangeProfession(event: DropdownChangeEvent) {
        this.selectedProfession = event.value;
        this.formIsValid();
    }

    onChangeCompany(event: Event) {
        this.formIsValid();
    }

    onChangeSituation(event: DropdownChangeEvent) {
        this.selectedSituation = event.value;
        this.formIsValid();
    }

    onChangeRevenue(event: Event) {
        this.formIsValid();
    }

    onChangeLastJob(event: Event) {
        this.formIsValid();
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    formIsValid() {
        this.continueButtonDisabled = !this.formGroup.valid;
    }

    //-------------------------//
    //--------NAVIGATION-------//
    //-------------------------//

    onClicButtonBack() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'working-situation');
        this.router.navigate(['/main/step/house-type'], { relativeTo: this.route });
    }

    onClicButtonContinue() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'working-situation');
        if(this.formGroup.value.workStatus == WorkingStatus.WORKING) {
            this.router.navigate(['/main/step/contract-type'], { relativeTo: this.route });
        } else {
            this.router.navigate(['/main/step/additional-data-first'], { relativeTo: this.route });
        }
    }

    ngOnDestroy() {}
}
